import {
  Box,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import UserAvatar from "../../common/UserAvatar";
import { format, parseISO } from "date-fns";
import React, { useEffect, useRef } from "react";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import toast from "../../../helpers/toast";
import {
  FilledButton,
} from "../../customComponents/styled/styledButtons";
import { StyledTableCell } from "../../customComponents/styled/styledTable";
import { useGetClassesQuery } from "../classroom/classApiSlice";
import {
  useGetAttendanceQuery,
  useCreateAttendanceMutation,
  useClearAttendanceMutation,
} from "./attendanceApiSlice";

function AttendanceSheet({ myClasses, activeTab }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const activeClassId = myClasses[activeTab]?.id;
  const [page, setPage] = React.useState(0);
  const PER_PAGE = 10;

  const { data: students, isLoading: fetchingStudents } = useGetClassesQuery(
    `${activeClassId}/students`,
    { skip: !activeClassId }
  );

  const { data: attendanceList, isLoading: fetchingAttendance } =
    useGetAttendanceQuery(activeClassId, { skip: !activeClassId });

  const [createAttendance, { isLoading: creatingAttendance }] =
    useCreateAttendanceMutation();

  const [clearAttendance, { isLoading: clearingAttendance }] =
    useClearAttendanceMutation();

  const [attendanceBody, setAttendanceBody] = React.useState([]);

  useEffect(() => {
    setPage(0);
  }, [activeTab]);

  useEffect(() => {
    setAttendanceBody(
      students?.map((student) => ({
        student_id: student.id,
        days_absent:
          attendanceList?.length > 0
            ? attendanceList.find((item) => item.student.id === student.id)
                ?.days_absent
            : 0,
        updated_at:
          attendanceList?.length > 0
            ? attendanceList.find((item) => item.student.id === student.id)
                ?.updated_at
            : new Date().toISOString(),
      })) || []
    );
  }, [students, attendanceList]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setAttendanceBody((prevState) => {
      return prevState.map((item) => {
        if (item.student_id === parseInt(name)) {
          return { ...item, days_absent: parseInt(value) };
        }
        return item;
      });
    });
  };

  const handleIncrement = (event) => {
    const { name } = event.currentTarget;
    setAttendanceBody((prevState) =>
      prevState.map((item) =>
        item.student_id === parseInt(name)
          ? { ...item, days_absent: (item.days_absent || 0) + 1 }
          : item
      )
    );
  };

  const handleDecrement = (event) => {
    const { name } = event.currentTarget;
    setAttendanceBody((prevState) =>
      prevState.map((item) =>
        item.student_id === parseInt(name)
          ? { ...item, days_absent: (item.days_absent || 0) - 1 }
          : item
      )
    );
  };

  const handleSubmitRef = useRef(null);

  useEffect(() => {
    handleSubmitRef.current = handleSubmit;
  });

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        handleSubmitRef.current?.();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleSubmit = async () => {
    const body = {
      classroom_id: myClasses[activeTab].id,
      attendance: attendanceBody,
    };
    try {
      const response = await createAttendance(body).unwrap();
      if (response.status === "success") {
        toast.success(response.message);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(error.message || "something went wrong");
    }
  };

  const handleReset = async () => {
    const body = {
      classroom_id: myClasses[activeTab].id,
    };
    try {
      const response = await clearAttendance(body).unwrap();
      if (response.status === "success") {
        toast.success(response.message);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(error.message || "something went wrong");
    }
  };

  return (
    <div>
      {(fetchingStudents || fetchingAttendance) && <LinearProgress />}
      {isMobile ? (
        <Box sx={{ pb: 10 }}>
          {students?.slice(page * PER_PAGE, (page + 1) * PER_PAGE)?.map((student) => {
            const studentAttendance = attendanceBody?.find(
              (attendance) => attendance.student_id === student.id
            );
            return (
              <Box
                key={student.id}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 1.5,
                  mb: 1.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Box sx={{ mr: 1 }}>
                    <UserAvatar src={student.image} name={`${student.first_name || ""} ${student.last_name || ""}`.trim()} />
                  </Box>
                  <Typography
                    sx={{
                      textTransform: "capitalize",
                      fontSize: "1rem",
                      lineHeight: "1.2rem",
                      color: "primary.main",
                      "& > span": {
                        fontSize: "12px",
                        color: "text.secondary",
                        fontStyle: "italic",
                      },
                    }}
                  >
                    {student.first_name + " " + student.last_name} <br />
                    <span>{student.reg_number}</span>
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Last updated: {studentAttendance?.updated_at
                    ? format(parseISO(studentAttendance.updated_at), "MMM dd, yyyy")
                    : "-"}
                </Typography>

                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    component="input"
                    type="number"
                    sx={{
                      width: "80px",
                      height: "35px",
                      padding: "1px 8px",
                    }}
                    name={student.id}
                    value={studentAttendance?.days_absent}
                    onChange={handleChange}
                  />
                  <FilledButton name={student.id} onClick={handleIncrement}>
                    +
                  </FilledButton>
                  <FilledButton name={student.id} onClick={handleDecrement}>
                    -
                  </FilledButton>
                </Stack>
              </Box>
            );
          })}
        </Box>
      ) : (
        <TableContainer sx={{ pb: 4, overflowX: "auto" }}>
          <Table sx={{ width: "100%", boxShadow: "none" }}>
            <TableHead>
              <TableRow>
                <StyledTableCell>student</StyledTableCell>
                <StyledTableCell>last updated</StyledTableCell>
                <StyledTableCell>absence</StyledTableCell>
                <StyledTableCell></StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {students?.slice(page * PER_PAGE, (page + 1) * PER_PAGE)?.map((student) => (
                <TableRow
                  key={student.id}
                  sx={{
                    padding: "10px 2px",
                    "& th": {
                      padding: "8px ",
                    },
                  }}
                >
                  <StyledTableCell component="th" scope="row">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Box sx={{ mr: 1 }}>
                        <UserAvatar src={student.image} name={`${student.first_name || ""} ${student.last_name || ""}`.trim()} />
                      </Box>
                      <Typography
                        sx={{
                          textTransform: "capitalize",
                          fontSize: "1.1rem",
                          lineHeight: "1.2rem",
                          color: "primary.main",
                          "& > span": {
                            fontSize: "12px",
                            color: "#333",
                            fontStyle: "italic",
                          },
                        }}
                      >
                        {student.first_name + " " + student.last_name} <br />
                        <span>{student.reg_number}</span>
                      </Typography>
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell component="th" scope="row">
                    {attendanceBody?.find(
                      (item) => item.student_id === student.id
                    )?.updated_at &&
                      format(
                        parseISO(
                          attendanceBody?.find(
                            (item) => item.student_id === student.id
                          )?.updated_at
                        ),
                        "MMM dd, yyyy"
                      )}
                  </StyledTableCell>

                  <StyledTableCell align="left">
                    <Box
                      component="input"
                      type="number"
                      sx={{
                        width: "50px",
                        height: "35px",
                        padding: "1px 2px",
                      }}
                      name={student.id}
                      value={
                        attendanceBody?.find(
                          (attendance) => attendance.student_id === student.id
                        )?.days_absent
                      }
                      onChange={handleChange}
                    />
                  </StyledTableCell>

                  <StyledTableCell align="left">
                    <Stack spacing={1} direction={{ md: "column", lg: "row" }}>
                      <FilledButton name={student.id} onClick={handleIncrement}>
                        +
                      </FilledButton>
                      <FilledButton name={student.id} onClick={handleDecrement}>
                        -
                      </FilledButton>
                    </Stack>
                  </StyledTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <TablePagination
        component="div"
        count={students?.length ?? 0}
        page={page}
        rowsPerPage={PER_PAGE}
        rowsPerPageOptions={[PER_PAGE]}
        onPageChange={(_, newPage) => setPage(newPage)}
      />
      {isMobile ? (
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: (zIndex) => zIndex.appBar,
            backgroundColor: "background.paper",
            borderTop: "1px solid",
            borderColor: "divider",
            px: 2,
            py: 1,
          }}
        >
          <Stack
            spacing={1}
            direction="row"
            justifyContent="center"
            sx={{
              "& .btn-icon": {
                fontSize: "14px",
              },
            }}
          >
            {creatingAttendance ? (
              <LinearProgress sx={{ width: 28, alignSelf: "center" }} />
            ) : (
              <Tooltip title="Save changes">
                <FilledButton onClick={handleSubmit}>
                  <SaveIcon className="btn-icon" />
                </FilledButton>
              </Tooltip>
            )}
            {clearingAttendance ? (
              <LinearProgress sx={{ width: 28, alignSelf: "center" }} />
            ) : (
              <Tooltip title="Reset attendance">
                <FilledButton onClick={handleReset}>
                  <RestartAltIcon className="btn-icon" />
                </FilledButton>
              </Tooltip>
            )}
          </Stack>
        </Box>
      ) : (
        <Stack
          spacing={1}
          direction="row"
          sx={{
            "& .btn-icon": {
              fontSize: "14px",
            },
          }}
        >
          {creatingAttendance ? (
            <LinearProgress sx={{ width: 28, alignSelf: "center" }} />
          ) : (
            <Tooltip title="Save changes">
              <FilledButton onClick={handleSubmit}>
                <SaveIcon className="btn-icon" />
              </FilledButton>
            </Tooltip>
          )}
          {clearingAttendance ? (
            <LinearProgress sx={{ width: 28, alignSelf: "center" }} />
          ) : (
            <Tooltip title="Reset attendance">
              <FilledButton onClick={handleReset}>
                <RestartAltIcon className="btn-icon" />
              </FilledButton>
            </Tooltip>
          )}
        </Stack>
      )}
    </div>
  );
}

export default AttendanceSheet;
